import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaDiecinueveM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout paddings
      const padding = 24;
      let y = padding;

      // compute left/right widths early so title fits inside the left column
      const usableW = Math.max(0, width - padding * 2);
      const interGap = 8; // gap between left block and right text column
      const leftAreaW = Math.round(usableW * 0.95);
      const rightAreaW = Math.max(0, usableW - leftAreaW - interGap);
      const leftAreaX = padding;
      const rightAreaX = leftAreaX + leftAreaW + interGap;

      // draw left (gray) and right (white) column backgrounds before the title
      ctx.fillStyle = opts.leftAreaBg || '#ffffff';
      ctx.fillRect(leftAreaX, 0, leftAreaW, height);

      ctx.fillStyle = opts.rightAreaBg || '#ffffff';
      ctx.fillRect(rightAreaX, 0, rightAreaW, height);

      // title square + title (placed inside the left column with a small inner inset)
      const titleFontSize = opts.titleFontSize || 50;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 0.9);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 18;
      const squareText = opts.titleSquareText || '3';
      const innerLeftPad = 8;
      const squareX = leftAreaX + innerLeftPad;

      // square (inside left column)
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // title (wrapped/justified) constrained to left column
      const title = opts.title || props.title || '';
      const titleX = squareX + squareSize + squareGap;
      const titleMaxW = Math.min(
        typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 1200,
        Math.max(0, leftAreaW - (squareSize + squareGap) - innerLeftPad - 12)
      );

      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });

        let yPos = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, yPos);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, yPos);
          }
          yPos += lineH;
        }
        return yPos;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // subtitle under title
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 30;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        y = Math.max(y + squareSize, afterSubY) + 24;
      } else {
        y = Math.max(y + squareSize, afterTitleY) + 24;
      }

      // -----------------------------
      // Paragraph list area (left column)
      // -----------------------------
      const defaultItems = [
        {
          Label: 'Medellín, la segunda ciudad capital del país, después de Bogotá, con el mayor número de víctimas',
          texto: [
            'Se posee registro desde 1985 de 772.904 víctimas del conflicto que han hecho su declaración en el distrito de Medellín.',
            'En 2024 el número de víctimas que declaró en el distrito y fue incluida en el RUV, aumentó 22% respecto al 2023.  En total, para el 2024 se han incluido 14.478  víctimas, en contraste con 11.799 víctimas en 2023.'
          ]
        },
        {
          Label: 'La población víctima presenta características diferenciales que acentúan su vulnerabilidad ante el conflicto armado:',
          texto: [
            '389.492 (50,4%) mujeres  y  383.377 (49,6%) hombres.',
            '13.108 NARP (4.5%)',
            '1.715 indígenas (0.6%)'
          ]
        },
        {
          Label: 'Aproximadamente 67% de las víctimas que han declarado en Medellín, han sido desplazadas forzosamente.',
          texto: [
            'En el Distrito de Medellín se posee registro de 520.878 víctimas que han declarado desplazamiento forzado.',
            '144.142 víctimas han sido expulsadas en el distrito.'
          ]
        }
      ];

      const items = Array.isArray(props.data) ? props.data : defaultItems;

      // left content area for paragraph rendering
      const leftContentX = leftAreaX + innerLeftPad;
      const leftContentW = Math.max(0, leftAreaW - innerLeftPad * 2);
      let cursorY = y + 40;
      const labelSize = typeof opts.itemLabelSize === 'number' ? opts.itemLabelSize : 30;
      const textSize = typeof opts.itemTextSize === 'number' ? opts.itemTextSize : 28;
      const labelLineH = Math.round(labelSize * 1.25);
      const textLineH = Math.round(textSize * 1.4);
      const itemGap = 12;

      const wrapLines = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = '';
        for (let w of words) {
          const test = cur ? cur + ' ' + w : w;
          if (c.measureText(test).width <= maxW) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      const wrapLinesWithMeta = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w);
            curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w);
            curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w];
            curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });
        return lines;
      };

      const drawJustifiedLine = (c, words, wordsWidth, x, y0, maxW) => {
        if (!words || words.length <= 1) {
          c.fillText((words || []).join(' '), x, y0);
          return;
        }
        const spaceW = c.measureText(' ').width;
        const gaps = words.length - 1;
        const extra = (maxW - wordsWidth) / gaps;
        let cx = x;
        for (let j = 0; j < words.length; j++) {
          const w = words[j];
          c.fillText(w, cx, y0);
          cx += c.measureText(w).width + spaceW + extra;
        }
      };

      const palette = opts.palette || ['#ef4444', '#06b6d4', '#f59e0b', '#10b981', '#6366f1', '#a78bfa'];

      ctx.fillStyle = opts.itemLabelColor || '#0f172a';
      for (let i = 0; i < items.length; i++) {
        const it = items[i] || {};

        // Label (bold) with colored square to the left
        ctx.font = `bold ${labelSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        const labelLines = wrapLinesWithMeta(ctx, it.Label || '', leftContentW - 28); // leave room for square + gap
        const squareSizeLabel = Math.min(14, Math.round(labelSize * 0.5));
        const squareGap = 8;
        const labelTextX = leftContentX + squareSizeLabel + squareGap;
        const squareYAdjust = Math.round((labelLineH - squareSizeLabel) / 2);

        for (let li = 0; li < labelLines.length; li++) {
          const line = labelLines[li];
          const isLast = li === labelLines.length - 1;
          if (li === 0) {
            // draw colored square for this item
            ctx.fillStyle = palette[i % palette.length] || '#06b6d4';
            ctx.fillRect(leftContentX, cursorY + squareYAdjust, squareSizeLabel, squareSizeLabel);

            // draw first line of label indented after square
            ctx.fillStyle = opts.itemLabelColor || '#0f172a';
            if (!isLast && line.words.length > 1) {
              drawJustifiedLine(ctx, line.words, line.wordsWidth, labelTextX, cursorY, leftContentW - 28);
            } else {
              ctx.fillText(line.words.join(' '), labelTextX, cursorY);
            }
          } else {
            // subsequent wrapped lines: align with label text (indented)
            ctx.fillStyle = opts.itemLabelColor || '#0f172a';
            if (!isLast && line.words.length > 1) {
              drawJustifiedLine(ctx, line.words, line.wordsWidth, labelTextX, cursorY, leftContentW - 28);
            } else {
              ctx.fillText(line.words.join(' '), labelTextX, cursorY);
            }
          }
          cursorY += labelLineH;
        }

        cursorY += 4; // small gap after label

        // texto array lines (regular text, render each as paragraph line, with bullets)
        ctx.font = `${textSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.itemTextColor || '#374151';
        const bullets = Array.isArray(it.texto) ? it.texto : (it.texto ? [String(it.texto)] : []);
        for (let t of bullets) {
          const tabExpanded = String(t).replace(/\t/g, ' ');
          const lines = wrapLinesWithMeta(ctx, tabExpanded, leftContentW - 18); // leave space for bullet
          // draw bullet on first line
          if (lines.length) {
            ctx.fillText('•', leftContentX, cursorY);
            const first = lines[0];
            if (lines.length > 1 && first.words.length > 1) {
              drawJustifiedLine(ctx, first.words, first.wordsWidth, leftContentX + 14, cursorY, leftContentW - 18);
            } else {
              ctx.fillText(first.words.join(' '), leftContentX + 14, cursorY);
            }
            cursorY += textLineH;
            for (let k = 1; k < lines.length; k++) {
              const line = lines[k];
              const isLast = k === lines.length - 1;
              if (!isLast && line.words.length > 1) {
                drawJustifiedLine(ctx, line.words, line.wordsWidth, leftContentX + 14, cursorY, leftContentW - 18);
              } else {
                ctx.fillText(line.words.join(' '), leftContentX + 14, cursorY);
              }
              cursorY += textLineH;
            }
          } else {
            ctx.fillText('•', leftContentX, cursorY);
            cursorY += textLineH;
          }
        }

        cursorY += itemGap;
      }

      // leave some breathing room before logo/footer
      y = cursorY + 8;

      // optional logo bottom-right
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_catorce_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});